'use client';

import type { FC, ReactNode } from 'react';
import type { IconProps } from './Icon.types';
import { Flex } from '@/_components';
import {
  ArrowBendDownRight,
  ArrowBendUpRight,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRightIcon,
  ArrowsClockwise,
  ArrowsIn,
  ArrowsLeftRight,
  ArrowsOut,
  Article,
  Bell,
  BellRinging,
  BellSlash,
  BookBookmark,
  BookOpenText,
  Briefcase,
  Browser,
  Browsers,
  Buildings,
  Calendar,
  Cards,
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretDown,
  CaretLeft,
  CaretLineDown,
  CaretLineUp,
  CaretRight,
  CaretUp,
  ChartBarHorizontal,
  Chat,
  ChatCircle,
  ChatTeardropDots,
  Check,
  CheckCircle,
  CheckSquare,
  Checks,
  Circle,
  CirclesThree,
  CirclesThreePlus,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Copy,
  CornersOut,
  Cpu,
  Cube,
  CurrencyDollar,
  CursorClick,
  CursorText,
  Dot,
  DotsSixVertical,
  DotsThree,
  DotsThreeOutline,
  Download,
  DownloadSimple,
  EnvelopeSimple,
  Eraser,
  Export,
  Eye,
  EyeClosed,
  File,
  FileCode,
  FileMagnifyingGlass,
  FilePlus,
  FileX,
  Files,
  FlowArrow,
  Folder,
  FolderLock,
  FolderOpen,
  FolderPlus,
  FolderUser,
  Funnel,
  Gear,
  GearFine,
  GridFour,
  Hash,
  Heart,
  House,
  IdentificationBadge,
  Info,
  Key,
  Lightbulb,
  Lightning,
  Link,
  List,
  ListChecksIcon,
  ListDashes,
  ListMagnifyingGlass,
  ListNumbers,
  Lock,
  LockKey,
  LockSimpleOpen,
  MagnifyingGlass,
  Minus,
  MinusIcon,
  MinusSquare,
  Monitor,
  NewspaperClipping,
  NoteBlank,
  PaperPlaneRight,
  Paperclip,
  PenNib,
  Pencil,
  PencilSimple,
  PlayCircle,
  Plug,
  Plugs,
  Plus,
  PlusCircle,
  Printer,
  Prohibit,
  Pulse,
  QrCode,
  Question,
  RadioButton,
  Receipt,
  Robot,
  Scroll,
  Share,
  Shield,
  ShieldCheck,
  ShieldSlash,
  Signature,
  Siren,
  Sliders,
  Sparkle,
  Spinner,
  SpinnerGap,
  SquaresFour,
  StopCircle,
  Tag,
  Textbox,
  ThumbsDown,
  ThumbsUp,
  Trash,
  Tray,
  Upload,
  User,
  UserCircle,
  UserCircleGear,
  UserCirclePlus,
  UserPlus,
  Users,
  Warning,
  WarningDiamond,
  WarningCircle,
  WebhooksLogo,
  Wrench,
  X,
  XCircle,
} from '@phosphor-icons/react';
import { getClasses } from '@/_utils/classnames';

export const Icon: FC<IconProps> = ({
  internalIconWidth,
  className,
  color,
  name,
  onClick,
  size,
}) => {
  const iconClasses: string = getClasses(className, {
    'text-accent': color === 'accent',
    'text-caution': color === 'caution',
    'text-critical': color === 'critical',
    'text-disabled': color === 'disabled',
    'text-primary': color === 'primary',
    'text-secondary': color === 'secondary',
    'text-success': color === 'success',
  });

  const getIcon = (): ReactNode => {
    const params: any = {
      size,
      weight: !!internalIconWidth
        ? internalIconWidth
        : size <= 12
          ? 'bold'
          : 'regular',
    };

    // prettier-ignore
    switch (name) {
      case 'arrow-bend-down-right': return <ArrowBendDownRight {...params}/>;
      case 'arrow-bend-up-right': return <ArrowBendUpRight {...params}/>;
      case 'arrow-counter-clockwise': return <ArrowCounterClockwise {...params}/>;
      case 'arrow-down': return <ArrowDown {...params}/>;
      case 'arrow-left': return <ArrowLeft {...params}/>;
      case 'arrow-right': return <ArrowRight {...params}/>;
      case 'arrow-up': return <ArrowUp {...params}/>;
      case 'arrow-up-right': return <ArrowUpRightIcon {...params}/>;
      case 'arrows-in': return <ArrowsIn {...params}/>;
      case 'arrows-out': return <ArrowsOut {...params}/>;
      case 'article': return <Article {...params}/>;
      case 'bell': return <Bell {...params} />;
      case 'bell-disabled': return <BellSlash {...params} />;
      case 'bell-ringing': return <BellRinging {...params} />;
      case 'book-bookmark': return <BookBookmark {...params}/>;
      case 'book-open-text': return <BookOpenText {...params}/>;
      case 'briefcase': return <Briefcase {...params}/>;
      case 'browser': return <Browser {...params}/>;
      case 'browsers': return <Browsers {...params}/>;
      case 'buildings': return <Buildings {...params}/>;
      case 'calendar': return <Calendar {...params}/>;
      case 'cards': return <Cards {...params}/>;
      case 'caret-double-left': return <CaretDoubleLeft {...params} weight='bold'/>;
      case 'caret-double-right': return <CaretDoubleRight {...params} weight='bold'/>;
      case 'caret-down': return <CaretDown {...params} weight='bold'/>;
      case 'caret-left': return <CaretLeft {...params} weight='bold'/>;
      case 'caret-line-down': return <CaretLineDown {...params} weight='bold'/>;
      case 'caret-line-up': return <CaretLineUp {...params} weight='bold'/>;
      case 'caret-right': return <CaretRight {...params} weight='bold'/>;
      case 'caret-up': return <CaretUp {...params} weight='bold'/>;
      case 'chart-bar-horizontal': return <ChartBarHorizontal {...params}/>;
      case 'chat': return <Chat {...params}/>;
      case 'chat-teardrop-dots': return <ChatTeardropDots {...params}/>;
      case 'chats': return <ChatCircle {...params}/>;
      case 'check': return <Check {...params}/>;
      case 'check-circle': return <CheckCircle {...params}/>;
      case 'check-circle-filled': return <CheckCircle {...params} weight='fill'/>;
      case 'check-square': return <CheckSquare {...params} weight='fill'/>;
      case 'checks': return <Checks {...params}/>;
      case 'circle': return <Circle {...params}/>;
      case 'circles-three': return <CirclesThree {...params}/>;
      case 'circles-three-plus': return <CirclesThreePlus {...params}/>;
      case 'clipboard': return <Clipboard {...params}/>;
      case 'clock': return <Clock {...params}/>;
      case 'cloud': return <Cloud {...params}/>;
      case 'code': return <Code {...params}/>;
      case 'copy': return <Copy {...params}/>;
      case 'corners-out': return <CornersOut {...params}/>;
      case 'cpu': return <Cpu {...params} weight='bold'/>;
      case 'cube': return <Cube {...params}/>;
      case 'cursor-click': return <CursorClick {...params}/>;
      case 'cursor-text': return <CursorText {...params}/>;
      case 'dashboard': return <SquaresFour {...params}/>;
      case 'dollar': return <CurrencyDollar {...params}/>;
      case 'dot': return <Dot {...params} />;
      case 'dots-six-vertical': return <DotsSixVertical {...params}/>;
      case 'dots-three': return <DotsThree {...params} weight='bold'/>;
      case 'dots-three-outline': return <DotsThreeOutline {...params}/>;
      case 'download': return <DownloadSimple {...params}/>;
      case 'download-drive': return <Download {...params}/>;
      case 'email': return <EnvelopeSimple {...params}/>;
      case 'envelope': return <EnvelopeSimple {...params}/>;
      case 'eraser': return <Eraser {...params}/>;
      case 'export': return <Export {...params}/>;
      case 'eye': return <Eye {...params}/>;
      case 'eye-closed': return <EyeClosed {...params}/>;
      case 'eye-filled': return <Eye {...params} weight='fill'/>;
      case 'file': return <File {...params}/>;
      case 'file-code': return <FileCode {...params}/>;
      case 'file-magnifying-glass': return <FileMagnifyingGlass {...params}/>;
      case 'file-plus': return <FilePlus {...params}/>;
      case 'file-x': return <FileX {...params}/>;
      case 'files': return <Files {...params}/>;
      case 'flow-arrow': return <FlowArrow {...params}/>;
      case 'folder': return <Folder {...params}/>;
      case 'folder-fill': return <Folder {...params} weight='fill'/>;
      case 'folder-fill-open': return <FolderOpen {...params} weight='fill'/>;
      case 'folder-lock': return <FolderLock {...params}/>;
      case 'folder-open': return <FolderOpen {...params}/>;
      case 'folder-plus': return <FolderPlus {...params}/>;
      case 'folder-user': return <FolderUser {...params}/>;
      case 'funnel': return <Funnel {...params}/>;
      case 'gear': return <Gear {...params}/>;
      case 'gear-fine': return <GearFine {...params}/>;
      case 'grab': return <DotsSixVertical {...params}/>;
      case 'grid-four': return <GridFour {...params}/>;
      case 'hash': return <Hash {...params}/>;
      case 'heart': return <Heart {...params}/>;
      case 'home': return <House {...params}/>;
      case 'identification-badge': return <IdentificationBadge {...params}/>;
      case 'info': return <Info {...params}/>;
      case 'key': return <Key {...params}/>;
      case 'lightbulb': return <Lightbulb {...params}/>;
      case 'lightning': return <Lightning {...params}/>;
      case 'link': return <Link {...params}/>;
      case 'list': return <List {...params}/>;
      case 'list-check': return <ListChecksIcon {...params} weight='bold'/>;
      case 'list-dashes': return <ListDashes {...params}/>;
      case 'list-numbers': return <ListNumbers {...params}/>;
      case 'list-search': return <ListMagnifyingGlass {...params}/>;
      case 'lock': return <Lock {...params}/>;
      case 'lock-key': return <LockKey {...params}/>;
      case 'lock-open': return <LockSimpleOpen {...params}/>;
      case 'minus': return <Minus {...params}/>;
      case 'minus-icon': return <MinusIcon {...params} weight='bold'/>;
      case 'minus-square': return <MinusSquare {...params} weight='bold'/>;
      case 'monitor': return <Monitor {...params}/>;
      case 'newspaper-clipping': return <NewspaperClipping {...params}/>;
      case 'nib': return <PenNib {...params}/>;
      case 'note': return <NoteBlank {...params}/>;
      case 'paperclip': return <Paperclip {...params} />;
      case 'pencil': return <Pencil {...params}/>;
      case 'pencil-simple': return <PencilSimple {...params}/>;
      case 'pencil-simple-filled': return <PencilSimple {...params} weight='fill'/>;
      case 'play-circle': return <PlayCircle {...params}/>;
      case 'plug': return <Plug {...params}/>;
      case 'plugs': return <Plugs {...params}/>;
      case 'plus': return <Plus {...params}/>;
      case 'plus-circle': return <PlusCircle {...params} />;
      case 'printer': return <Printer {...params}/>;
      case 'prohibit': return <Prohibit {...params}/>;
      case 'pulse': return <Pulse {...params}/>;
      case 'qr-code': return <QrCode {...params}/>;
      case 'question': return <Question {...params}/>;
      case 'radio-button': return <RadioButton {...params}/>;
      case 'receipt': return <Receipt {...params}/>;
      case 'refresh': return <ArrowsClockwise {...params}/>;
      case 'robot': return <Robot {...params}/>;
      case 'rotate': return <ArrowClockwise {...params}/>;
      case 'scroll': return <Scroll {...params}/>;
      case 'search': return <MagnifyingGlass {...params}/>;
      case 'send': return <PaperPlaneRight {...params}/>;
      case 'share': return <Share {...params}/>;
      case 'shield': return <Shield {...params} />;
      case 'shield-check': return <ShieldCheck {...params} />;
      case 'shield-slash': return <ShieldSlash {...params}/>;
      case 'signature': return <Signature {...params}/>;
      case 'siren': return <Siren {...params}/>;
      case 'sliders': return <Sliders {...params}/>;
      case 'sparkle': return <Sparkle {...params}/>;
      case 'spinner': return <Spinner {...params}/>;
      case 'spinner-gap': return <SpinnerGap {...params}/>;
      case 'stop-circle': return <StopCircle {...params}/>;
      case 'switch': return <ArrowsLeftRight {...params}/>;
      case 'tag': return <Tag {...params}/>;
      case 'text-box': return <Textbox {...params}/>;
      case 'thumbs-down': return <ThumbsDown {...params}/>;
      case 'thumbs-up': return <ThumbsUp {...params}/>;
      case 'trash': return <Trash {...params}/>;
      case 'tray': return <Tray {...params}/>;
      case 'upload': return <Upload {...params}/>;
      case 'user': return <User {...params} />;
      case 'user-circle': return <UserCircle {...params}/>;
      case 'user-circle-gear': return <UserCircleGear {...params}/>;
      case 'user-circle-plus': return <UserCirclePlus {...params}/>;
      case 'user-plus': return <UserPlus {...params}/>;
      case 'users': return <Users {...params}/>;
      case 'warning': return <Warning {...params}/>;
      case 'warning-diamond': return <WarningDiamond {...params}/>;
      case 'warning-outline': return <Warning {...params} />;
      case 'warning-circle': return <WarningCircle {...params} />;
      case 'webhooks': return <WebhooksLogo {...params}/>;
      case 'wrench': return <Wrench {...params}/>;
      case 'x': return <X {...params} />;
      case 'x-circle': return <XCircle {...params} />;
      case 'x-circle-line': return <XCircle {...params}/>;

      default: return null;
    }
  };

  const icon: ReactNode = getIcon();

  return (
    <Flex isCentered={true} className={iconClasses} onClick={onClick}>
      {icon}
    </Flex>
  );
};
